import React, { useEffect, useMemo, useState } from "react";
import "../styles/appstore.css";
import { Link } from "react-router-dom";
import { ref, listAll } from "firebase/storage";
import { storage } from "./cms/utils/firebase";
import firebaseConfig from "../fb.json";
import appsMeta from "../applications.json";

interface App {
  name: string;
  title: string;
  imgSrc?: string;
  category?: string;
  platforms: string[];
  web?: string;
}

const BUCKET = (firebaseConfig as any).storageBucket as string;

const appImages = require.context(
  "../assets/appstore",
  false,
  /\.(png|jpe?g|svg)$/
);

// Optional display overrides (nice title / web link) keyed by app name. The
// *list* of apps is discovered from Storage, so a new app shows up the moment
// its first build is uploaded — even without an entry here.
const META: Record<string, App> = Object.fromEntries(
  (appsMeta as App[]).map((a) => [a.name, a])
);

const prettify = (name: string) =>
  name.replace(/[-_]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const storageIcon = (name: string) =>
  `https://firebasestorage.googleapis.com/v0/b/${BUCKET}/o/${encodeURIComponent(
    `builds/_icons/${name}.png`
  )}?alt=media`;

// Icon resolution: bundled asset (assets/appstore/<name>.png) → the app's real
// icon uploaded to Storage by CI (builds/_icons/<name>.png) → letter placeholder.
const AppIcon: React.FC<{ name: string; title: string }> = ({ name, title }) => {
  const candidates = useMemo(() => {
    const list: string[] = [];
    try {
      list.push(appImages(`./${name}.png`));
    } catch {}
    list.push(storageIcon(name));
    return list;
  }, [name]);
  const [idx, setIdx] = useState(0);

  if (idx >= candidates.length) {
    return (
      <div className="appstore__card-icon appstore__card-icon--placeholder">
        {title.charAt(0)}
      </div>
    );
  }
  return (
    <img
      src={candidates[idx]}
      alt={title}
      className="appstore__card-icon"
      onError={() => setIdx((i) => i + 1)}
    />
  );
};

// Discover apps + platforms from the public build artifacts in Storage:
//   plists/<app>_ios[...].ipa.plist   → iOS
//   builds/<app>_android[...].apk     → Android
// App names contain no underscores, so the token after the first "_" is the
// platform. Channel-isolated agent builds live under builds/<channel>/… (a
// prefix, not a top-level item) and never carry an "_ios"/"_android" token at
// position 1, so they're naturally excluded.
const discoverApps = async (): Promise<App[]> => {
  const platforms = new Map<string, Set<string>>();
  const add = (name: string, platform: string) => {
    if (!name) return;
    if (!platforms.has(name)) platforms.set(name, new Set());
    platforms.get(name)!.add(platform);
  };

  const [plists, builds] = await Promise.all([
    listAll(ref(storage, "plists")).catch(() => ({ items: [] as any[] })),
    listAll(ref(storage, "builds")).catch(() => ({ items: [] as any[] })),
  ]);

  for (const it of plists.items) {
    const parts = it.name.replace(/\.ipa\.plist$/, "").split("_");
    if (parts[1] === "ios") add(parts[0], "ios");
  }
  for (const it of builds.items) {
    if (!it.name.endsWith(".apk")) continue;
    const parts = it.name.replace(/\.apk$/, "").split("_");
    if (parts[1] === "android") add(parts[0], "android");
  }

  return Array.from(platforms.entries())
    .map(([name, plats]) => ({
      name,
      title: META[name]?.title ?? prettify(name),
      web: META[name]?.web,
      platforms: Array.from(plats).sort(),
    }))
    .sort((a, b) => a.title.localeCompare(b.title));
};

const Appstore = () => {
  const [apps, setApps] = useState<App[] | null>(null);

  useEffect(() => {
    discoverApps()
      .then((list) =>
        // Fall back to the static list if Storage discovery turns up nothing.
        setApps(list.length ? list : (appsMeta as App[]))
      )
      .catch(() => setApps(appsMeta as App[]));
  }, []);

  return (
    <div className="appstore">
      <div className="appstore__header">
        <h1 className="appstore__title">Apps</h1>
        <p className="appstore__subtitle">
          Builds & web links for all projects
        </p>
      </div>

      {apps === null ? (
        <p className="appstore__subtitle">Loading…</p>
      ) : (
        <div className="appstore__grid">
          {apps.map((app, index) => (
            <div
              key={app.name}
              className="appstore__card"
              style={{ animationDelay: `${index * 0.04}s` }}
            >
              <Link
                to={`/validation?app=${app.name}`}
                className="appstore__card-iconlink"
                title={`${app.title} — validation screenshots`}
              >
                <AppIcon name={app.name} title={app.title} />
              </Link>
              <span className="appstore__card-name">{app.title}</span>

              <div className="appstore__card-links">
                <Link
                  to={`/validation?app=${app.name}`}
                  className="appstore__link"
                  title={`${app.title} — validation screenshots`}
                >
                  <svg
                    className="appstore__web-icon"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="3" y="3" width="18" height="18" rx="2" />
                    <circle cx="9" cy="9" r="2" />
                    <path d="m21 15-3.5-3.5L13 16" />
                  </svg>
                  Screenshots
                </Link>
                {app.platforms.includes("ios") && (
                  <Link
                    to={`/updraft?app=${app.name}&platform=ios`}
                    className="appstore__link"
                    title={`${app.title} — iOS build`}
                  >
                    <span className="appstore__platform-badge ios" />
                    iOS
                  </Link>
                )}
                {app.platforms.includes("android") && (
                  <Link
                    to={`/updraft?app=${app.name}&platform=android`}
                    className="appstore__link"
                    title={`${app.title} — Android build`}
                  >
                    <span className="appstore__platform-badge android" />
                    Android
                  </Link>
                )}
                {app.web && (
                  <a
                    href={app.web}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="appstore__link appstore__link--web"
                    title={`${app.title} — Website`}
                  >
                    <svg
                      className="appstore__web-icon"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <path d="M2 12h20" />
                      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                    </svg>
                    Web
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Appstore;
